import {
  Button,
  Page,
  SearchInput,
  SimplePagination,
  Table,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { SortBy, SortDirection, SortStatus } from 'lib-shared-legacy-types';
import type { WidgetFlowT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMount, useSetState } from 'react-use';

import { AppDispatch } from 'bootstrap.store';
import type { FlowChatActionT } from 'components/powerChatFlow/api/types';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import { withFlows, WithFlowsInjectedPropsT, withWidgets } from 'hocs';
import type { WithWidgetsInjectedPropsT } from 'hocs';
import { WigetSortKey } from 'hocs/widgets/api/types';
import { useNotifications } from 'hooks/useNotifications';

import { AddWidgetModal } from './addWidgetModal';
import { ChatWidgetDotsMenu } from './chatWidgetDotsMenu';
import { RenameWidgetModal } from './renameWidgetModal';

type SortByValue = WigetSortKey.Name | WigetSortKey.UpdatedAt;

export type PropsT = WithWidgetsInjectedPropsT &
  WithFlowsInjectedPropsT<FlowChatActionT> &
  ChatWidgetsDispatchProps;

type StateT = {
  isConfirmModalOpen: boolean;
  isCreateModalOpen: boolean;
  isRenameModalOpen: boolean;
  selectedWidget: null | WidgetFlowT;
  sortStatus: SortStatus | null;
};

const COLUMN_COUNT = 3;
const FETCH_DELAY = 1600;

const ChatWidgetsImpl = (props: PropsT): JSX.Element => {
  const navigate = useNavigate();
  const notify = useNotifications();

  const hasChannelSetupAccess = useHasAccess(
    'settings.integration.channel.setup',
    {
      fallbackRole: 'admin',
    },
  );
  const hasFlowReadAccess = useHasAccess('settings.flow:r');
  const hasFlowWriteAccess = useHasAccess('settings.flow:w');

  const [state, setState] = useSetState<StateT>({
    isConfirmModalOpen: false,
    isCreateModalOpen: false,
    isRenameModalOpen: false,
    selectedWidget: null,
    sortStatus: {
      direction: SortDirection.Ascending,
      sortBy: WigetSortKey.Name,
    },
  });

  useMount(() => {
    props.widgets.getItems();
  });

  const handleSearchSubmit = (searchTerm: string) => {
    const { widgets } = props;
    if (searchTerm !== widgets.params?.search) {
      widgets.paramsChange({ search: searchTerm }, 0);
      ampli.chatSearchApplied();
    }
  };

  const handleSearchToggle = (isOpen: boolean) => {
    if (isOpen) {
      ampli.chatSearchOpened();
    } else {
      const { widgets } = props;
      ampli.chatSearchClosed();
      if (widgets.params.search) {
        widgets.paramsChange({ search: '' }, 0);
      }
    }
  };

  const handleSortChange = (newSortStatus: SortStatus | null) => {
    const { widgets } = props;
    const { sortStatus } = state;

    if (!isEqual(sortStatus, newSortStatus)) {
      setState({ sortStatus: newSortStatus });

      if (newSortStatus === null) {
        widgets.paramsChange({
          sort_by: null,
        });
        return;
      }

      const { direction, sortBy } = newSortStatus;
      const sortByParam = `${sortBy}:${direction}` as SortBy<SortByValue>;

      widgets.paramsChange({
        sort_by: sortByParam,
      });
    }
  };

  const handleGoToWidgetSettings = (widgetId: string) => {
    navigate(`/settings/chat/${widgetId}`);
  };

  const handleGoToChatFlow = (flowId: string) => {
    navigate(`/settings/chat/flow/${flowId}`);
  };

  const handleGoToAuditLogs = (flowId: string) => {
    navigate(`/settings/chat/flow/${flowId}/history`);
  };

  const openCreateWidgetModal = () => {
    const { addChatWidgetClicked } = props;

    ampli.chatFormOpened({
      mode: 'create',
    });
    addChatWidgetClicked();
    setState({ isCreateModalOpen: true });
  };

  const closeCreateWidgetModal = () => {
    const { addChatWidgetClosed } = props;

    addChatWidgetClosed();
    setState({ isCreateModalOpen: false });
  };

  const openRenameWidgetModal = (widget: WidgetFlowT) => {
    ampli.chatFormOpened({
      mode: 'update',
    });
    setState({ isRenameModalOpen: true, selectedWidget: widget });
  };

  const closeRenameWidgetModal = () => {
    setState({ isRenameModalOpen: false });
  };

  const handleDuplicateWidget = async (widget: WidgetFlowT) => {
    const { flows, widgets } = props;
    const widgetCopyName = `${widget.name} (copy)`;
    const widgetCopy: WidgetFlowT = {
      ...widget,
      name: widgetCopyName,
    };
    const widgetFlowId = widget.flows[0].id;

    try {
      const [{ data: newWidget }, { data: widgetFlow }] = await Promise.all([
        widgets.create(widgetCopy),
        flows.getItem(widgetFlowId),
      ]);

      const flowCopy = {
        ...widgetFlow,
        location: newWidget.id,
        name: newWidget.name,
        number: newWidget.id,
      };
      const { data: newFlow } = await flows.create(flowCopy);

      notify({
        title: 'Widget copied',
        text: `Widget was succesfully copied as "${widgetCopyName}"`,
        className: 'success',
        time: 5000,
        icon: 'done',
      });

      handleGoToChatFlow(newFlow.id);
    } catch {
      notify({
        title: 'An error occurred',
        text: `An error occurred when trying to copy the "${widget.name}" widget`,
        icon: 'exclamation',
        className: 'danger',
      });
    }
  };

  const openConfirmDeleteWidgetModal = (widget: WidgetFlowT) => {
    setState({
      isConfirmModalOpen: true,
      selectedWidget: widget,
    });
  };

  const handleDeleteWidget = () => {
    const { widgets } = props;
    const { selectedWidget } = state;

    if (selectedWidget) {
      widgets
        .remove(selectedWidget.id)
        .then(() => {
          notify({
            title: 'Widget deleted',
            text: `"${selectedWidget.name}" widget was succesfully deleted`,
            className: 'success',
            time: 5000,
            icon: 'done',
          });

          closeConfirmDeleteWidgetModal();
          // fetch widget items with delay to make sure
          // the new list is updated
          setTimeout(() => {
            handleRefresh();
          }, FETCH_DELAY);
        })
        .catch(() => {
          closeConfirmDeleteWidgetModal();
          notify({
            title: 'An error occurred',
            text: `An error occurred when trying to delete widget "${selectedWidget.name}"`,
            className: 'danger',
            icon: 'exclamation',
          });
        });
    }
  };

  const closeConfirmDeleteWidgetModal = () => {
    setState({
      isConfirmModalOpen: false,
      selectedWidget: null,
    });
  };

  const goToPreviousPage = () => {
    ampli.chatPaginationPreviousClicked();
    props.widgets.previousPage();
  };

  const goToNextPage = () => {
    ampli.chatPaginationNextClicked();
    props.widgets.nextPage();
  };

  const handleRefresh = () => {
    props.widgets.getItems();
  };

  const renderLoadingStatus = () => {
    const { widgets } = props;

    if (widgets.getItemsAsyncStatus === 'failed') {
      return (
        <Table.Tr>
          <Table.Td colSpan={COLUMN_COUNT}>
            Something went wrong...
            <Button variant="tertiary" onClick={handleRefresh}>
              Try again
            </Button>
          </Table.Td>
        </Table.Tr>
      );
    }

    return <TableLoadingRow colSpan={COLUMN_COUNT} />;
  };

  const renderRow = (widget: WidgetFlowT) => {
    return (
      <Table.Tr key={widget.id}>
        <Table.Td>{widget.name}</Table.Td>
        <Table.Td>
          {widget.updated_at ? (
            <Timestamp relative time={widget.updated_at} />
          ) : (
            '-'
          )}
        </Table.Td>
        <Table.Td>
          <ChatWidgetDotsMenu
            deleteWidget={openConfirmDeleteWidgetModal}
            duplicateWidget={handleDuplicateWidget}
            goToChatFlow={handleGoToChatFlow}
            goToAuditLogs={handleGoToAuditLogs}
            goToWidgetSettings={handleGoToWidgetSettings}
            renameWidget={openRenameWidgetModal}
            widget={widget}
          />
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderBody = () => {
    const { widgets } = props;
    const { getItemsAsyncStatus, items } = widgets;

    if (getItemsAsyncStatus !== 'succeeded') {
      return renderLoadingStatus();
    }

    if (items.length) {
      return items.map(renderRow);
    }

    return (
      <Table.Tr>
        <Table.Td colSpan={COLUMN_COUNT}>No widgets found.</Table.Td>
      </Table.Tr>
    );
  };

  const renderFoot = () => {
    const { widgets } = props;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = widgets;
    const hasFoot = hasNextPage || hasPreviousPage;

    if (!hasFoot) return null;

    return (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={COLUMN_COUNT}>
            <SimplePagination
              previousButtonClick={goToPreviousPage}
              previousButtonDisabled={!hasPreviousPage}
              nextButtonClick={goToNextPage}
              nextButtonDisabled={!hasNextPage}
              loading={getItemsAsyncStatus === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );
  };

  const {
    isConfirmModalOpen,
    isCreateModalOpen,
    isRenameModalOpen,
    selectedWidget,
    sortStatus,
  } = state;

  return (
    <>
      <Page.Section
        title="All widgets"
        controls={
          <>
            <SearchInput
              onToggle={handleSearchToggle}
              onSubmit={handleSearchSubmit}
            />

            {hasChannelSetupAccess &&
              hasFlowReadAccess &&
              hasFlowWriteAccess && (
                <Button
                  onClick={openCreateWidgetModal}
                  id="add-chat-widget-btn"
                >
                  Add widget
                </Button>
              )}
          </>
        }
      >
        <Table
          framed
          stickyHeader
          stickyFooter
          headerWithBackground
          sortingProps={{
            onSortChange: handleSortChange,
            sortStatus,
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th columnId={WigetSortKey.Name}>Name</Table.Th>
              <Table.Th columnId={WigetSortKey.UpdatedAt}>Updated</Table.Th>
              <Table.Th columnId={null} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{renderBody()}</Table.Tbody>
          {renderFoot()}
        </Table>
      </Page.Section>

      <AddWidgetModal
        goToFlow={handleGoToChatFlow}
        isOpen={isCreateModalOpen}
        onClose={closeCreateWidgetModal}
      />

      {selectedWidget && (
        <RenameWidgetModal
          initialValues={{ name: selectedWidget.name }}
          isOpen={isRenameModalOpen}
          onClose={closeRenameWidgetModal}
          refreshPage={handleRefresh}
          widget={selectedWidget}
        />
      )}

      <ConfirmModal
        cancel={closeConfirmDeleteWidgetModal}
        cancelText="Cancel"
        confirm={handleDeleteWidget}
        confirmText="Yes, delete it now"
        headline="Are you sure?"
        isShown={isConfirmModalOpen}
        text={`Are you sure you want to delete the widget
            "${selectedWidget?.name}"?`}
      />
    </>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  addChatWidgetClicked: () => dispatch({ type: 'ADD_CHAT_WIDGET_CLICKED' }),
  addChatWidgetClosed: () => dispatch({ type: 'ADD_CHAT_WIDGET_CLOSED' }),
});

export type ChatWidgetsDispatchProps = ReturnType<typeof mapDispatchToProps>;

export const ChatWidgets = connect<void, ChatWidgetsDispatchProps>(
  null,
  mapDispatchToProps,
)(
  withWidgets(withFlows<FlowChatActionT, PropsT>(ChatWidgetsImpl), {
    initialParams: {
      sort_by: `${WigetSortKey.Name}:asc` as SortBy<SortByValue>,
    },
  }),
);
